import { isRecord, trimmed } from './parse';

/** 直接采纳。查询、SSE 和点击确认都收成这一个 action。 */
export const DIRECT_ACCEPT_ACTION = 'direct_accept';

/** 告诉 AI 哪里不好。不发消息，选项内容写入输入框 placeholder。 */
export const PROVIDE_PROPOSAL_FEEDBACK_ACTION = 'provide_proposal_feedback';

export interface ProposalWireFlags {
  status?: string;
  locked?: boolean;
  selectedAction?: string;
}

export interface NormalizedProposalAcceptance {
  status: string;
  locked: boolean;
  selectedAction?: string;
  accepted: boolean;
}

const ACCEPTED_STATUSES = new Set(['accepted', 'online_applied']);

const isAcceptedText = (status?: string) =>
  Boolean(status && ACCEPTED_STATUSES.has(status.trim().toLowerCase()));

const isDirectAccept = (action?: string) =>
  action?.toLowerCase() === DIRECT_ACCEPT_ACTION;

const looksLikeProposal = (item: Record<string, unknown>) =>
  Boolean(
    trimmed(item.proposal_id) ||
      (trimmed(item.status) && Array.isArray(item.changes))
  );

/**
 * 正文有时就是提案，有时包在 data / Data 里。
 * 已是提案或带 proposal 字段时不动，避免把提案自己的 data 当成外层包装。
 */
const proposalSource = (source: Record<string, unknown>) => {
  if (looksLikeProposal(source) || isRecord(source.proposal)) return source;
  const nested = [source.data, source.Data].find(isRecord);
  if (!nested) return source;
  if (
    looksLikeProposal(nested) ||
    isRecord(nested.proposal) ||
    trimmed(nested.status)
  ) {
    return nested;
  }
  return source;
};

/** 提案对象本身。外层包装先拆掉，再取 proposal 字段。changes 在这一层。 */
export const readProposalBody = (
  source: Record<string, unknown> | null | undefined
): Record<string, unknown> | undefined => {
  if (!source) return undefined;
  const body = proposalSource(source);
  return isRecord(body.proposal) ? body.proposal : body;
};

/**
 * 从一帧或查询正文里读 status / locked / selected_action。
 * 提案对象和它外层都看：任意一层是 accepted 或 direct_accept 就保留这个信号。
 */
export const readProposalWireFlags = (
  source: Record<string, unknown> | null | undefined
): ProposalWireFlags => {
  if (!source) return {};
  const body = proposalSource(source);
  const nested = isRecord(body.proposal) ? body.proposal : undefined;
  const layers = [nested, body].filter(isRecord);
  const statuses = layers
    .map((layer) => trimmed(layer.status))
    .filter((item): item is string => Boolean(item));
  const selected = layers
    .map(
      (layer) => trimmed(layer.selected_action) || trimmed(layer.selectedAction)
    )
    .filter((item): item is string => Boolean(item));
  const status = statuses.find(isAcceptedText) || statuses[0];
  const selectedAction = selected.find(isDirectAccept) || selected[0];
  const locked = layers.some((layer) => layer.locked === true);
  return {
    ...(status ? { status } : {}),
    ...(selectedAction ? { selectedAction } : {}),
    ...(locked ? { locked: true } : {}),
  };
};

/**
 * status 为 accepted，或 selected_action 为 direct_accept，都是采纳成功。
 * 只有 locked、或选了别的 action，不算成功。成功时收成同一组字段。
 */
export const normalizeProposalAcceptance = (
  flags: ProposalWireFlags,
  fallbackStatus = 'draft'
): NormalizedProposalAcceptance => {
  const status = flags.status?.trim() || '';
  const selected = flags.selectedAction?.trim() || '';
  const accepted = isAcceptedText(status) || isDirectAccept(selected);
  if (accepted) {
    return {
      status: 'accepted',
      locked: true,
      selectedAction: DIRECT_ACCEPT_ACTION,
      accepted: true,
    };
  }
  return {
    status: status || fallbackStatus,
    locked: flags.locked === true || Boolean(selected),
    ...(selected ? { selectedAction: selected } : {}),
    accepted: false,
  };
};

export const acceptanceFields = (
  flags: ProposalWireFlags,
  fallbackStatus = 'draft'
) => {
  const normalized = normalizeProposalAcceptance(flags, fallbackStatus);
  return {
    status: normalized.status,
    ...(normalized.locked ? { locked: true as const } : {}),
    ...(normalized.selectedAction
      ? { selectedAction: normalized.selectedAction }
      : {}),
  };
};

/** 查询结果盖过会话快照。没带回的 locked / selectedAction 不沿用旧值。 */
export const queriedAcceptance = (
  currentStatus: string,
  lookedUp: ProposalWireFlags
) => {
  const fields = acceptanceFields(
    {
      status: lookedUp.status ?? currentStatus,
      locked: lookedUp.locked,
      selectedAction: lookedUp.selectedAction,
    },
    currentStatus || 'draft'
  );
  return {
    status: fields.status,
    locked: fields.locked,
    selectedAction: fields.selectedAction,
  };
};

export const isProposalAccepted = (
  proposal?: { status?: string; selectedAction?: string } | null
) =>
  Boolean(
    proposal &&
      normalizeProposalAcceptance({
        status: proposal.status,
        selectedAction: proposal.selectedAction,
      }).accepted
  );
